import { Component, OnInit, inject, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { Dialog } from '@capacitor/dialog';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { AppCommonModule } from 'src/app/common.module';
import { Room } from 'src/app/models/room.model';
import { SiteArea } from 'src/app/models/site-area.model';
import { SiteAreasService } from 'src/app/services/site-areas.service';
import { ModalShellComponent } from 'src/app/ui/modal-shell/modal-shell.component';
import { RoomComponent } from 'src/app/sites/rooms/room.component';
import { EditAreaDialogComponent } from '../edit-area/edit-area.dialog';

const DELIMITER = '-';

@Component({
  selector: 'app-add-area-dialog',
  templateUrl: './add-area.dialog.html',
  styleUrls: ['./add-area.dialog.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AppCommonModule,
    RoomComponent,
    ModalShellComponent,
  ],
})
export class AddAreaDialogComponent implements OnInit {
  private formBuilder = inject(FormBuilder);
  private modalCtrl = inject(ModalController);
  private siteAreasService = inject(SiteAreasService);
  private translate = inject(TranslateService);

  parent = input<SiteArea>(null);
  root = input<SiteArea>();

  parentName: string;
  itemFormGroup: FormGroup;
  formGroup: FormGroup;
  roomFormGroup: FormGroup;

  rooms: Room[];

  item = signal<SiteArea>(null);
  isRoot = signal<boolean>(null);
  hasEdit = signal(false);
  type = signal<'area' | 'room' | 'common'>(null);
  multiple = signal(false);

  ngOnInit(): void {
    this.item.set(this.parent());
    this.hasEdit.set(this.item() != null && this.root() != null);
    this.parentName = this.item()?.name;
    this.isRoot.set(this.item()?.guid != null);

    this.formGroup = this.formBuilder.group({
      name: [this.item()?.name],
      areas: this.formBuilder.array(this.item()?.children.map((it) => this.formBuilder.control(it.name)) ?? []),
      numbers: [''],
      size: [null],
      volume: [null],
      height: [null],
      internal_doors: [null],
      external_doors: [null],
      windows: [null],
    });

    this.roomFormGroup = this.formBuilder.group({
      rooms: this.formBuilder.array(this.item()?.rooms.map((it) => this.formBuilder.control(it.id)) ?? []),
    });
    this.rooms = this.item()?.rooms?.length > 0 ? this.item().rooms : null;

    if (this.item() == null) {
      this.type.set('area');
      this.addItem();
    } else if (this.item().children?.length > 0) {
      this.type.set('area');
    }
  }

  selectAddArea() {
    this.type.set('area');
    this.addItem();
  }

  getFields() {
    return this.formGroup.get('areas') as FormArray;
  }

  getRooms() {
    return this.roomFormGroup.get('rooms') as FormArray;
  }

  addItem() {
    this.getFields().push(this.formBuilder.control(''));
  }

  deleteItem(index: number) {
    this.getFields().removeAt(index);
  }

  onDeleteRoom(index: number) {
    this.getRooms().removeAt(index);
    this.rooms.splice(index, 1);
  }

  onAreaRoom(rooms: Room[]) {
    if (this.rooms == null) {
      this.rooms = [];
    }
    rooms.forEach((room) => {
      this.getRooms().push(this.formBuilder.control(room.id));
      this.rooms.push(room);
    });
    this.type.set(null);
  }

  deleteSelf() {
    Dialog.confirm({
      message: this.translate.instant('general.delete') + '?',
      okButtonTitle: this.translate.instant('general.yes'),
      cancelButtonTitle: this.translate.instant('general.no'),
    }).then((dialog) => {
        if (dialog.value) {
        this.modalCtrl.dismiss({ delete: true });
      }
    });
  }

  onEdit() {
    this.modalCtrl.create({
      component: EditAreaDialogComponent,
      componentProps: { item: this.item() },
      cssClass: ['stack-modal'],
    }).then((m) => {
      m.present();

      m.onDidDismiss().then((data) => {
        if (data.data) {
          this.item.update((val) => Object.assign(val, data.data));
          this.siteAreasService.update(this.root());
        }
      });
    });
  }

  toggleMultiple() {
    this.multiple.update((val) => !val);
  }

  save() {
    const item = this.formGroup.value;
    if (this.multiple()) {
      const numbers = this.formGroup.get('numbers').value as string;
      if (numbers.includes(DELIMITER)) {
        const start = numbers.split(DELIMITER)[0];
        const end = numbers.split(DELIMITER)[1];
        for (let i = +start; i <= +end; i++) {
          const id = `${i}`;
          item.areas.push(id);
        }
      }
    }
    const areaDefaults = {
      size: this.toOptionalNumber(item.size),
      volume: this.toOptionalNumber(item.volume),
      height: this.toOptionalNumber(item.height),
      internal_doors: this.toOptionalNumber(item.internal_doors),
      external_doors: this.toOptionalNumber(item.external_doors),
      windows: this.toOptionalNumber(item.windows),
    };
    const hasDefaults = Object.values(areaDefaults).some((value) => value != null);

    this.modalCtrl.dismiss({
      areas: item.areas.filter((it: string) => it.length > 0),
      name: item.name,
      areaDefaults: hasDefaults ? areaDefaults : undefined,
    });
  }

  saveRooms() {
    const items = this.roomFormGroup.value;
    const rooms = this.rooms.map((room, i) => {
      room.id = items.rooms[i];
      return room;
    });
    this.modalCtrl.dismiss({ rooms });
  }

  onCancel() {
    this.type.set(null);
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }

  private toOptionalNumber(value: unknown) {
    if (value == null || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
}
